/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
	Elements,
	PaymentElement,
	useStripe,
	useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, Loader2 } from 'lucide-react';

import allStates from '../../../register/allStates';
import * as actions from '../../../../actions/billing';
import { getStripePromise } from '@/lib/stripeClient';
import { isPendingSignup, hasSignupSubscription } from '@/components/signup/activation';
import { PendingBillingNotice } from '@/components/signup/LiveOnboardingPage';
import SignupSubscriptionNotice from '@/components/signup/SignupSubscriptionNotice';

const loadingMessages = [
	'Contacting card provider...',
	'Confirming card details...',
	'Attempting to create payment...',
	'Verifying payment...',
	'Finalizing...',
];
const REACTIVATE_PENDING_KEY = 'billing_reactivate_pending_v1';

const defaultErrors = {
	address: '',
	city: '',
	region: '',
};

const RegisterTextField = (textFieldProps) => (
	<Input
		variant="outline"
		className="w-full"
		style={{ ...textFieldProps.style }}
		{...textFieldProps}
	/>
);

function ReactivateFormInner({ reactivateAccount, closeDialog }) {
	const [loading, setLoading] = useState(false);
	const [loadingStep, setLoadingStep] = useState(0);
	const [loadingMessage, setLoadingMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [showSuccess, setShowSuccess] = useState(false);
	const [address, setAddress] = useState('');
	const [city, setCity] = useState('');
	const [region, setRegion] = useState('');
	const [errors, setErrors] = useState(defaultErrors);

	const intervalRef = useRef(null);
	const stripe = useStripe();
	const elements = useElements();

	useEffect(() => {
		return () => clearInterval(intervalRef.current);
	}, []);

	const validateInputs = () => {
		let isError = false;
		const newErrors = { ...errors };

		if (!address) {
			newErrors.address = 'Please enter your business address';
			isError = true;
		} else {
			newErrors.address = '';
		}

		if (!city) {
			newErrors.city = 'Please enter your city';
			isError = true;
		} else {
			newErrors.city = '';
		}

		if (!region) {
			newErrors.region = 'Select your state or province';
			isError = true;
		} else {
			newErrors.region = '';
		}

		if (isError) {
			setErrors(newErrors);
			return { error: true };
		}

		return { error: false };
	};

	const startTimer = () => {
		setLoadingStep(0);
		let step = 0;
		intervalRef.current = setInterval(() => {
			if (step < 4) {
				step += 1;
				setLoadingStep(step);
				setLoadingMessage(loadingMessages[step]);
			}
		}, 3000);
	};

	const submit = async (e) => {
		if (e?.preventDefault) e.preventDefault();

		const valid = validateInputs();
		if (valid.error) return;

		if (!stripe || !elements) return;

		setLoading(true);
		if (typeof window !== 'undefined') {
			sessionStorage.setItem(
				REACTIVATE_PENDING_KEY,
				JSON.stringify({ address, region, city })
			);
		}

		const { error, setupIntent } = await stripe.confirmSetup({
			elements,
			confirmParams: {
				return_url: `${window.location.origin}/settings/billing`,
			},
			redirect: 'if_required',
		});

		if (error) {
			if (typeof window !== 'undefined') {
				sessionStorage.removeItem(REACTIVATE_PENDING_KEY);
			}
			setErrorMessage(
				error.message || 'Error processing your credit card.'
			);
			setLoading(false);
			return;
		}

		startTimer();

		const response = await reactivateAccount({
			address,
			region,
			city,
			paymentMethodId: setupIntent.payment_method,
		});
		if (typeof window !== 'undefined') {
			sessionStorage.removeItem(REACTIVATE_PENDING_KEY);
		}

		if (response.error) {
			setErrorMessage(response.message);
			setShowSuccess(false);
			setLoading(false);
			return;
		}

		if (closeDialog) closeDialog();

		setShowSuccess(true);
		setErrorMessage('');
		setLoading(false);
	};

	const handleCancel = () => {
		clearInterval(intervalRef.current);
		if (closeDialog) closeDialog();
	};

	const handleSuccessConfirm = () => {
		window.location.reload();
	};

	if (showSuccess) {
		return (
			<div className="flex flex-1 flex-col min-h-0">
				<div className="flex-1 overflow-y-auto min-h-0">
					<div className="rounded-lg border bg-muted/20 p-5 space-y-3">
						<div className="flex items-center gap-2">
							<CheckCircle2 className="h-5 w-5 text-green-500" />
							<p className="font-semibold text-foreground">
								Reactivation successful
							</p>
						</div>
						<p className="text-sm text-muted-foreground">
							You have upgraded to Chekkit Business.
						</p>
						<p className="text-sm text-muted-foreground">
							If you need support, use the chat bubble in the
							bottom left corner, or email
							{' '}
							<a
								href="mailto:support@chekkit.io"
								className="text-primary underline underline-offset-2"
							>
								support@chekkit.io
							</a>
							.
						</p>
					</div>
				</div>
				<DialogFooter className="flex-shrink-0 pt-4 border-t mt-4">
					<div className="flex flex-row justify-end mr-2.5">
						<Button onClick={handleSuccessConfirm}>Continue</Button>
					</div>
				</DialogFooter>
			</div>
		);
	}

	return (
		<div className="flex flex-1 flex-col min-h-0">
			<div className="flex-1 overflow-y-auto min-h-0">
				<div className="flex flex-col gap-4">
					<p className="text-lg font-bold tracking-tight text-foreground -mb-2">
						Reactivate Chekkit Business
					</p>

					<div className="space-y-4">
						<div className="space-y-1">
							<p className="text-sm font-medium text-foreground">
								Business Address
							</p>
							<p className="text-xs text-muted-foreground">
								Used for your billing details.
							</p>
						</div>
						<form
							className="flex flex-col w-full gap-4"
							noValidate
							autoComplete="off"
						>
							<div className="space-y-1">
								<RegisterTextField
									id="address"
									value={address}
									onChange={(e) => setAddress(e.target.value)}
									placeholder="Address"
								/>
								{errors.address ? (
									<p className="text-xs text-red-500">
										{errors.address}
									</p>
								) : null}
							</div>

							<div className="space-y-1">
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1">
										<RegisterTextField
											id="city"
											value={city}
											onChange={(e) =>
												setCity(e.target.value)
											}
											placeholder="City"
										/>
										{errors.city ? (
											<p className="text-xs text-red-500">
												{errors.city}
											</p>
										) : null}
									</div>

									<div className="space-y-1">
										<Select
											labelId="region-select"
											id="region-select"
											value={region}
											onValueChange={setRegion}
										>
											<SelectTrigger
												className="flex items-center"
												aria-label="Region"
											>
												<SelectValue placeholder="Select State / Province" />
											</SelectTrigger>
											<SelectContent>
												{allStates.map((state) => (
													<SelectItem
														key={
															state.abbreviation
														}
														value={state.name}
													>
														{state.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{errors.region ? (
											<p className="text-xs text-red-500">
												{errors.region}
											</p>
										) : null}
									</div>
								</div>
							</div>
						</form>
					</div>

					<div className="space-y-3">
						<div className="space-y-1">
							<p className="text-sm font-medium text-foreground">
								Payment Method
							</p>
							<p className="text-xs text-muted-foreground">
								Enter your card details to reactivate your
								subscription.
							</p>
						</div>
						<div className="rounded-lg border bg-background p-3">
							<PaymentElement />
						</div>
					</div>

					{errorMessage ? (
						<div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
							{errorMessage}
						</div>
					) : null}

					<div className="flex flex-col gap-2">
						<Button onClick={submit} disabled={loading || !stripe}>
							Reactivate for $149 / month*
						</Button>
						<p className="text-xs text-muted-foreground">
							*Plan includes 2,000 messages per month, additional
							messages billed at 3 cents per message
						</p>
						{loading && (
							<div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
								<Loader2 className="h-4 w-4 animate-spin" />
								<span>{loadingMessage || ''}</span>
							</div>
						)}
					</div>
				</div>
			</div>
			<DialogFooter className="flex-shrink-0 pt-4 border-t mt-4">
				<div className="flex flex-row justify-end mr-2.5">
					<Button
						variant="ghost"
						onClick={handleCancel}
						disabled={loading}
					>
						Back
					</Button>
				</div>
			</DialogFooter>
		</div>
	);
}

function ReactivateAccountForm({ reactivateAccount, closeDialog, auth }) {
	const [clientSecret, setClientSecret] = useState('');
	const pendingSignup = isPendingSignup(auth);
	const signupSubscription = hasSignupSubscription(auth);

	useEffect(() => {
		if (pendingSignup || signupSubscription) return;
		axios
			.post('/api/create-setup-intent')
			.then((res) => {
				if (res.data.clientSecret) {
					setClientSecret(res.data.clientSecret);
				}
			})
			.catch(() => {});
	}, [pendingSignup, signupSubscription]);
	if (pendingSignup) return <PendingBillingNotice />;
	if (signupSubscription) return <SignupSubscriptionNotice needsAttention={!auth?.activated} />;

	if (!clientSecret) {
		return (
			<div className="flex justify-center items-center py-8">
				<Loader2 className="size-6 animate-spin" />
			</div>
		);
	}

	return (
		<Elements stripe={getStripePromise()} options={{ clientSecret }}>
			<ReactivateFormInner
				reactivateAccount={reactivateAccount}
				closeDialog={closeDialog}
			/>
		</Elements>
	);
}

function mapStateToProps(state) {
	return {
		auth: state.auth,
	};
}

export default connect(mapStateToProps, actions)(ReactivateAccountForm);
